import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import Header from "../components/HeaderA";
import { translateServerMsg } from '../utils/serverMessages';

const translations = {
  el: {
    welcome: "ShareTheDeal | Καλώς ήρθατε! ",
    subWelcome: "Η εφαρμογή όπου οι έξυπνοι αγοραστές γίνονται μια κοινότητα!",
    feat1: "→ Ανακαλύψτε τις καλύτερες προσφορές γύρω σας.",
    feat2: "→ Μοιραστείτε τα δικά σας ευρήματα και βοηθήστε τους άλλους να εξοικονομήσουν περισσότερα.",
    slogan: "Γιατί μια καλή προσφορά είναι ακόμα καλύτερη όταν τη μοιράζεσαι!",
    username: "Όνομα χρήστη",
    password: "Κωδικός πρόσβασης",
    placeholderUser: "Εισάγετε όνομα χρήστη",
    placeholderPass: "Εισάγετε κωδικό",
    loginBtn: "Σύνδεση",
    noAccount: "Δεν έχετε λογαριασμό;",
    register: "Εγγραφή",
    failAlert: "Η σύνδεση απέτυχε. Παρακαλώ δοκιμάστε ξανά.",
    errorTitle: "Σφάλμα σύνδεσης",
    ok: "Εντάξει"
  },
  en: {
    welcome: "ShareTheDeal | Welcome!",
    subWelcome: "The app where smart shoppers become a community!",
    feat1: "→ Discover the best discounts around you.",
    feat2: "→ Share your own finds, and help others save more.",
    slogan: "Because a great deal is even better when it’s shared!",
    username: "Username",
    password: "Password",
    placeholderUser: "Enter username",
    placeholderPass: "Enter password",
    loginBtn: "Login",
    noAccount: "Don’t have an account?",
    register: "Register",
    failAlert: "Login failed. Please try again.",
    errorTitle: "Login error",
    ok: "OK"
  }
};

const Login = () => {
  const { login } = useContext(AuthContext);
  const [username, setUsername] = useState('');
  const [user_password, setPassword] = useState('');
  const navigate = useNavigate();

  // State for language preference - loads from localStorage (persists across sessions)
  const [lang, setLang] = useState(localStorage.getItem("app_lang") || localStorage.getItem("language") || "el");
  const [popup, setPopup] = useState(null);

  // Listen for language changes from HeaderA or other tabs/windows
  useEffect(() => {
    const handleStorageChange = () => {
      setLang(localStorage.getItem("app_lang") || localStorage.getItem("language") || "el");
    };

    // Listen for storage changes from other browser tabs or windows
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const t = translations[lang];

  const handleLogin = async (e) => {
    e.preventDefault();
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, user_password })
    });

    const data = await res.json();

    if (!res.ok) {
      setPopup({ title: t.errorTitle, msg: translateServerMsg(data.msg, lang) });
      setUsername('');
      setPassword('');
      return;
    }

    if (data.accessToken && data.refreshToken) {
      login(data.accessToken, data.refreshToken);
      // Navigate based on user role
      if (data.user.role === 'admin') {
        navigate('/adminmenu');
      } else {
        navigate('/customermenu');
      }
    } else {
      setPopup({ title: t.errorTitle, msg: t.failAlert });
      setUsername('');
      setPassword('');
    }
  };

  return (
    <div className="min-h-screen bg-custom-background flex flex-col">
      <Header />

      <main id="main-content" className="flex-1 flex flex-col md:flex-row items-center justify-center px-4 md:px-16 py-8 gap-12 md:gap-20">

        {/* Left Text Section */}
        <div className="max-w-md text-center md:text-left">
          <h1 id="login-heading" className="text-2xl md:text-3xl text-custom-letters font-semibold mb-4">{t.welcome}</h1>
          <p className="mb-2 text-custom-letters text-base md:text-lg">{t.subWelcome}</p>
          <p className="italic text-custom-letters mb-1 text-sm md:text-base">{t.feat1}</p>
          <p className="italic text-custom-letters mb-4 text-sm md:text-base">{t.feat2}</p>
          <p className="text-custom-letters font-bold text-xl md:text-2xl">{t.slogan}</p>
        </div>

        {/* Right Login Form */}
        <div className="flex justify-center w-full md:w-80">
          <form 
            onSubmit={handleLogin}
            aria-labelledby="login-heading"
            noValidate
            className="bg-gradient-to-b from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 p-6 rounded-3xl shadow-lg w-full max-w-md flex flex-col gap-4">
            
            <label htmlFor="login-username" className="text-white font-semibold">{t.username}</label>
            <input
              id="login-username"
              type="text"
              placeholder={t.placeholderUser}
              value={username}
              onChange={e => setUsername(e.target.value)}
              required
              autoComplete="username"
              className="p-2 rounded-lg text-black outline-none focus:ring-2 focus:ring-custom-header"
            />

            <label htmlFor="login-password" className="text-white font-semibold">{t.password}</label>
            <input
              id="login-password"
              type="password"
              placeholder={t.placeholderPass}
              value={user_password}
              onChange={e => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="p-2 rounded-lg text-black outline-none focus:ring-2 focus:ring-custom-header"
            />

            <button
              type="submit"
              className="bg-custom-header text-custom-letters font-bold py-2 rounded-lg mt-2 hover:bg-gray-200 transition">
              {t.loginBtn}
            </button>

            <p className="font-bold text-custom-letters mt-4 text-center text-sm md:text-base"> 
              {t.noAccount}{" "}
              <Link to="/register" className="text-custom-background font-semibold hover:underline">{t.register}</Link>
            </p>
          </form>
        </div>
      </main>

      {popup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000]">
          <div role="dialog" aria-modal="true" aria-labelledby="login-popup-title" className="bg-custom-header rounded-2xl p-6 w-80 shadow-xl text-center">
            <h3 id="login-popup-title" className="text-lg font-semibold text-custom-letters mb-3">{popup.title}</h3>
            <p className="text-sm text-custom-letters mb-6">{popup.msg}</p>
            <button
              onClick={() => setPopup(null)}
              className="px-6 py-2 rounded-full bg-gradient-to-r from-custom-grad1 to-custom-grad4 text-custom-letters font-bold">
              {t.ok}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;